// BUILD-046 Owner-requested polish (visual edits ledger, session edit 4.1 / 4.3): the small, muted REQUESTER BADGE ("By Julie · Oct 18") of a task row, and the
// "Requested" cell of the Compact table. What it says is requesterBadgeModel.ts's; this file draws it.
//
// The badge is keyboard focusable (tabIndex 0) and carries a complete accessible name. Its tooltip (role=tooltip, referenced by aria-describedby while it is
// shown) holds the full requester name and the full request date, plus "Manual task" for a manual task. It opens on hover AND on keyboard focus, stays open while
// the pointer is over it (hoverable), and closes on Escape, on blur and when the pointer leaves. It is drawn through a portal into <body> with fixed
// coordinates, so the table's overflow-hidden (rounded border) can never clip it; it is kept inside the viewport and flips above the badge near the bottom.
//
// Muted on purpose: text-gray-600 on gray-100 (about 6.9:1), small text, no bright colour, so the priority dot and word and an overdue date stand out.
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import type { RequesterBadge as RequesterBadgeData } from './requesterBadgeModel';

const BADGE_CLASS =
  'inline-flex max-w-full items-center rounded bg-gray-100 px-1.5 py-0.5 text-xs font-normal normal-case tracking-normal text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7fb8]';

const VIEWPORT_MARGIN = 8;

interface TipPosition {
  top: number;
  left: number;
  above: boolean;
}

export function RequesterBadge({ badge }: { badge: RequesterBadgeData }) {
  const tipId = useId();
  const badgeRef = useRef<HTMLSpanElement | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);
  const [overBadge, setOverBadge] = useState(false);
  const [overTip, setOverTip] = useState(false);
  const [focused, setFocused] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [position, setPosition] = useState<TipPosition | null>(null);
  const open = (overBadge || overTip || focused) && !dismissed;

  function place(): void {
    const anchor = badgeRef.current;
    const tip = tipRef.current;
    if (anchor === null || tip === null) return;
    const rect = anchor.getBoundingClientRect();
    const width = tip.offsetWidth;
    const height = tip.offsetHeight;
    const left = Math.max(VIEWPORT_MARGIN, Math.min(rect.left, window.innerWidth - width - VIEWPORT_MARGIN));
    const above = rect.bottom + height > window.innerHeight - VIEWPORT_MARGIN && rect.top - height > VIEWPORT_MARGIN;
    setPosition({ top: above ? rect.top - height : rect.bottom, left, above });
  }

  // Measured before paint, so the tooltip never flashes at the wrong place.
  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return undefined;
    }
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, badge.tooltip]);

  // Escape closes the tooltip whether it was opened by hover or by focus (WCAG 1.4.13 "dismissible").
  useEffect(() => {
    if (!open) return undefined;
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') setDismissed(true);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const tipStyle: CSSProperties = position === null ? { position: 'fixed', top: 0, left: 0, visibility: 'hidden' } : { position: 'fixed', top: position.top, left: position.left };

  return (
    <>
      <span
        ref={badgeRef}
        role="group"
        tabIndex={0}
        aria-label={badge.ariaLabel}
        aria-describedby={open ? tipId : undefined}
        data-testid="requester-badge"
        className={BADGE_CLASS}
        onMouseEnter={() => {
          setOverBadge(true);
          setDismissed(false);
        }}
        onMouseLeave={() => {
          setOverBadge(false);
          setDismissed(false);
        }}
        onFocus={() => {
          setFocused(true);
          setDismissed(false);
        }}
        onBlur={() => {
          setFocused(false);
          setDismissed(false);
        }}
      >
        <span className="truncate">{badge.text}</span>
      </span>
      {open &&
        createPortal(
          <div
            ref={tipRef}
            style={tipStyle}
            className={`z-[60] w-max max-w-sm ${position?.above === true ? 'pb-1' : 'pt-1'}`}
            onMouseEnter={() => setOverTip(true)}
            onMouseLeave={() => setOverTip(false)}
          >
            <div id={tipId} role="tooltip" data-testid="requester-tooltip" className="rounded-md bg-gray-900 px-2.5 py-1.5 text-xs font-normal normal-case leading-snug tracking-normal text-white shadow-lg">
              {badge.tooltipLines.map((line) => (
                <div key={line}>{line}</div>
              ))}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

/**
 * The "Requested" cell of the Compact table (column after Task): the badge, or an empty cell when the row shows none, so the columns stay aligned.
 * Hidden below 768 px, where rows stack.
 */
export function RequestedCell({ badge }: { badge: RequesterBadgeData | null }) {
  return (
    <div role="cell" className="hidden min-w-0 md:block" data-testid="requested-cell">
      {badge !== null && <RequesterBadge badge={badge} />}
    </div>
  );
}
